import axios from "axios";
import { serverUrl } from "../nestserver";

const auth = () => ({
  headers: {
    Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
  },
});

/** The backend wraps every success in { result: ... }. */
const unwrap = (res) => res?.data?.result;

/** Errors come back as { error: { errors: [...] } }, but not consistently. */
export function whatsappError(err, fallback = "Something went wrong") {
  return (
    err?.response?.data?.error?.errors?.[0] ||
    err?.response?.data?.error?.errors ||
    err?.response?.data?.message ||
    fallback
  );
}

// ── pickers ──────────────────────────────────────────────────────────────
// These are scoped server-side: an admin gets every class in the school, an
// instructor only their own. The UI does not need to branch on role.

export async function fetchSelectableClasses() {
  const res = await axios.get(`${serverUrl}/whatsapp/pickers/classes`, auth());
  return unwrap(res) || [];
}

export async function fetchSelectableInstructors(search) {
  const res = await axios.get(`${serverUrl}/whatsapp/pickers/instructors`, {
    ...auth(),
    params: search ? { search } : {},
  });
  return unwrap(res) || [];
}

export async function fetchClassParents(classId) {
  const res = await axios.get(
    `${serverUrl}/whatsapp/pickers/classes/${classId}/parents`,
    auth(),
  );
  return unwrap(res) || { students: [], parents: [], students_without_parent: 0 };
}

// ── business account (per-school WhatsApp credentials) ──────────────────

export async function fetchMyWhatsappAccount() {
  const res = await axios.get(
    `${serverUrl}/whatsapp-business-account/mine`,
    auth(),
  );
  return unwrap(res) || { connected: false, data: null };
}

export async function connectWhatsappAccount(payload) {
  const res = await axios.post(
    `${serverUrl}/whatsapp-business-account`,
    payload,
    auth(),
  );
  return unwrap(res);
}

// ── groups ───────────────────────────────────────────────────────────────

export async function fetchGroups(params = {}) {
  const res = await axios.get(`${serverUrl}/whatsapp/groups`, {
    ...auth(),
    params,
  });
  return unwrap(res) || { items: [], total: 0 };
}

export async function fetchGroup(id) {
  const res = await axios.get(`${serverUrl}/whatsapp/groups/${id}`, auth());
  return unwrap(res);
}

export async function fetchGroupPreview(id) {
  const res = await axios.get(
    `${serverUrl}/whatsapp/groups/${id}/preview`,
    auth(),
  );
  return unwrap(res);
}

export async function createGroup(payload) {
  const res = await axios.post(`${serverUrl}/whatsapp/groups`, payload, auth());
  return unwrap(res);
}

export async function updateGroup(id, payload) {
  const res = await axios.patch(
    `${serverUrl}/whatsapp/groups/${id}`,
    payload,
    auth(),
  );
  return unwrap(res);
}

export async function deleteGroup(id) {
  const res = await axios.delete(`${serverUrl}/whatsapp/groups/${id}`, auth());
  return unwrap(res);
}

export async function resyncGroup(id) {
  const res = await axios.post(
    `${serverUrl}/whatsapp/groups/${id}/resync`,
    {},
    auth(),
  );
  return unwrap(res);
}

export async function removeGroupMember(id, userId) {
  // userId is a raw phone number (may contain "+"/spaces) for campaign
  // groups, so this must be encoded — a no-op for ordinary Mongo ids.
  const res = await axios.delete(
    `${serverUrl}/whatsapp/groups/${id}/members/${encodeURIComponent(userId)}`,
    auth(),
  );
  return unwrap(res);
}

export async function addGroupMembers(id, payload) {
  const res = await axios.post(
    `${serverUrl}/whatsapp/groups/${id}/members`,
    payload,
    auth(),
  );
  return unwrap(res);
}

// ── messages ─────────────────────────────────────────────────────────────

export async function sendMessage(payload) {
  const res = await axios.post(
    `${serverUrl}/whatsapp/messages`,
    payload,
    auth(),
  );
  return unwrap(res);
}

export async function fetchMessages(params = {}) {
  const res = await axios.get(`${serverUrl}/whatsapp/messages`, {
    ...auth(),
    params,
  });
  return unwrap(res) || { items: [], total: 0 };
}

export async function fetchMessageRecipients(id, params = {}) {
  const res = await axios.get(
    `${serverUrl}/whatsapp/messages/${id}/recipients`,
    { ...auth(), params },
  );
  return unwrap(res) || { items: [], total: 0 };
}

export async function retryFailed(id) {
  const res = await axios.post(
    `${serverUrl}/whatsapp/messages/${id}/retry-failed`,
    {},
    auth(),
  );
  return unwrap(res);
}

// ── wallet (prepaid billing — ₹0.15 per message sent) ───────────────────

export async function fetchWalletBalance() {
  const res = await axios.get(`${serverUrl}/whatsapp-wallet/balance`, auth());
  return unwrap(res) || { balance_paise: 0 };
}

export async function fetchWalletStatement(params = {}) {
  const res = await axios.get(`${serverUrl}/whatsapp-wallet/statement`, {
    ...auth(),
    params,
  });
  return unwrap(res) || { data: [], total: 0, page: 1, limit: 20 };
}

/**
 * Reuses the existing payment-gateway Razorpay order/verify routes with
 * `purpose: 'WALLET_RECHARGE'` rather than a new endpoint. IMPORTANT: unlike
 * every other call in this file, `payment-gateway.controller.ts`'s order/
 * verify routes return the service result directly — not wrapped in
 * `{ result: ... }` — so these two read `res.data`, not `unwrap(res)`.
 */
export async function createWalletRechargeOrder(amountPaise, schoolId) {
  const res = await axios.post(
    `${serverUrl}/payment-gateway/order`,
    {
      amount: amountPaise,
      currency: "INR",
      schoolId,
      purpose: "WALLET_RECHARGE",
      notes: { info: "WhatsApp wallet recharge" },
    },
    auth(),
  );
  return res.data;
}

export async function verifyWalletRechargePayment(payload) {
  const res = await axios.post(
    `${serverUrl}/payment-gateway/verify`,
    payload,
    auth(),
  );
  return res.data;
}

// ── campaigns (admin-only — saved, reusable lists of raw phone numbers) ──
// A campaign is never sent to directly: picking one in the "Marketing" step
// of the group-creation modal turns its numbers into a real WhatsappGroup,
// which is then messaged like any other group/chat.

export async function createCampaign(payload) {
  const res = await axios.post(
    `${serverUrl}/whatsapp/campaigns`,
    payload,
    auth(),
  );
  return unwrap(res);
}

export async function fetchCampaigns() {
  const res = await axios.get(`${serverUrl}/whatsapp/campaigns`, auth());
  return unwrap(res) || { items: [] };
}

/** Full detail (name + every number) for the group-creation preview popup. */
export async function fetchCampaign(id) {
  const res = await axios.get(`${serverUrl}/whatsapp/campaigns/${id}`, auth());
  return unwrap(res);
}
